import LoadingLogo from '@/components/atoms/LoadingLogo';
import useTeamConcerns from '@/hooks/organization/useTeamConcerns';
import { extra, text } from '@/theme/colors/palettes';
import { TTeamConcernsRange } from '@/types/Organization.types';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import ConcernRow from './teamConcerns/ConcernRow';
import TeamConcernsLegend from './teamConcerns/TeamConcernsLegend';
import TeamConcernsModal from './teamConcerns/TeamConcernsModal';
import TeamConcernsRangeSelect from './teamConcerns/TeamConcernsRangeSelect';
import { buildCollapsedRows, COLLAPSED_TOP_COUNT, ROW_GAP } from './teamConcerns/helpers';

interface Props {
  defaultRange?: TTeamConcernsRange;
}

const TeamConcernsWidget = ({ defaultRange = 'last-6-months' }: Props) => {
  const [range, setRange] = useState<TTeamConcernsRange>(defaultRange);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, isRefetching } = useTeamConcerns(range);

  const allCategories = data?.categories ?? [];
  const collapsedRows = useMemo(() => buildCollapsedRows(allCategories), [allCategories]);
  const collapsedMaxTotal = useMemo(() => Math.max(1, ...collapsedRows.map((r) => r.total)), [collapsedRows]);

  const hasData = allCategories.length > 0;
  const hasMoreToExpand = allCategories.length > COLLAPSED_TOP_COUNT;

  return (
    <>
      <VStack
        width={'100%'}
        backgroundColor={extra.white}
        borderRadius={'12px'}
        boxShadow={'0px 8px 34px 0px #0041791A'}
        padding={'16px'}
        alignItems={'stretch'}
        gap={'8px'}
      >
        <HStack width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
          <Text variant={'urbanistBoldLarge'} color={text.darkBlue}>
            Team Concerns
          </Text>
          <HStack gap={'8px'} alignItems={'center'} justifyContent={'flex-end'} flex={1}>
            {isRefetching && <LoadingLogo width="24px" spinnerSize="24px" spacing="4px" showsText={false} />}
            <TeamConcernsRangeSelect range={range} onChange={setRange} />
          </HStack>
        </HStack>

        <TeamConcernsLegend />

        {isLoading && !data ? (
          <VStack height={'200px'} justifyContent={'center'}>
            <LoadingLogo width="42px" spinnerSize="42px" spacing="10px" showsText={false} />
          </VStack>
        ) : !hasData ? (
          <VStack height={'200px'} justifyContent={'center'}>
            <Text variant={'urbanistMediumRegular'} color={text.mediumGray} textAlign={'center'}>
              No team concerns for this range.
            </Text>
          </VStack>
        ) : (
          <VStack width={'100%'} alignItems={'stretch'} gap={`${ROW_GAP}px`} marginTop={'4px'}>
            {collapsedRows.map((row) => (
              <ConcernRow key={row.category} row={row} maxTotal={collapsedMaxTotal} totalPosition={'right'} />
            ))}
          </VStack>
        )}

        {hasData && hasMoreToExpand && (
          <HStack width={'100%'} justifyContent={'center'} marginTop={'10px'}>
            <Text
              as={'button'}
              variant={'urbanistSmallBold'}
              color={text.darkBlue}
              fontWeight={900}
              textTransform={'uppercase'}
              cursor={'pointer'}
              onClick={() => setIsModalOpen(true)}
            >
              View All Concerns
            </Text>
          </HStack>
        )}
      </VStack>

      <TeamConcernsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        categories={allCategories}
        range={range}
        onRangeChange={setRange}
      />
    </>
  );
};

export default TeamConcernsWidget;
